"use client";

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from "react";
import { BottomSheetWrapper } from "@/components/database/hooks/useBottomSheet";
import { Check, Minus, Plus, Search as SearchIcon } from "lucide-react";
import { blockColorConfig, type BlockColor } from "@/types/block";

// ─── Context ─────────────────────────────────────────────

interface ActionSheetContextValue {
  close: () => void;
}

const ActionSheetContext = createContext<ActionSheetContextValue | null>(null);

function useSheetContext() {
  const ctx = useContext(ActionSheetContext);
  if (!ctx) throw new Error("ActionSheet 하위에서만 사용 가능");
  return ctx;
}

// ─── useActionSheet hook ─────────────────────────────────

export interface UseActionSheetReturn {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

export function useActionSheet(): UseActionSheetReturn {
  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((v) => !v), []);
  return { isOpen, open, close, toggle };
}

// ─── Root ────────────────────────────────────────────────

export interface ActionSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  icon?: ReactNode;
  size?: "sm" | "md" | "lg";
  children: ReactNode;
}

export function ActionSheet({
  isOpen,
  onClose,
  title,
  icon,
  size = "sm",
  children,
}: ActionSheetProps) {
  return (
    <ActionSheetContext.Provider value={{ close: onClose }}>
      <BottomSheetWrapper
        isOpen={isOpen}
        onClose={onClose}
        title={title}
        icon={icon}
        size={size}
      >
        <div className="px-1.5 py-1.5 sm:px-2 sm:py-2">{children}</div>
      </BottomSheetWrapper>
    </ActionSheetContext.Provider>
  );
}

// ─── Item ────────────────────────────────────────────────

export interface ActionSheetItemProps {
  icon?: ReactNode;
  label: string;
  shortcut?: string;
  danger?: boolean;
  disabled?: boolean;
  /** true면 클릭 시 시트 자동 닫기 (기본 true) */
  autoClose?: boolean;
  /** 선택 상태 표시 (체크마크) */
  selected?: boolean;
  onClick?: () => void;
}

function Item({
  icon,
  label,
  shortcut,
  danger = false,
  disabled = false,
  autoClose = true,
  selected = false,
  onClick,
}: ActionSheetItemProps) {
  const { close } = useSheetContext();

  const handleClick = () => {
    if (disabled) return;
    onClick?.();
    if (autoClose) close();
  };

  return (
    <button
      onClick={handleClick}
      disabled={disabled}
      className={[
        "w-full flex items-center gap-3 px-3 rounded-lg text-sm text-left",
        "min-h-[44px] py-2.5",
        "transition-all duration-150 ease-out",
        "select-none",
        "focus-visible:outline-2 focus-visible:outline-[var(--color-pad-line-blue)] focus-visible:outline-offset-[-2px]",
        disabled
          ? "opacity-35 cursor-not-allowed"
          : danger
            ? [
                "text-red-600 dark:text-red-400",
                "hover:bg-red-500/8 active:bg-red-500/15",
                "active:scale-[0.98]",
              ].join(" ")
            : [
                "text-[var(--color-text-primary)]",
                "hover:bg-[var(--color-surface-hover)]",
                "active:bg-[var(--color-surface-hover)]",
                "active:scale-[0.98]",
              ].join(" "),
      ].join(" ")}
    >
      {icon && (
        <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center text-[var(--color-text-secondary)]">
          {icon}
        </span>
      )}
      <span className="flex-1 font-medium leading-snug">{label}</span>
      {selected && (
        <Check className="flex-shrink-0 w-4 h-4 text-[var(--color-accent-yellow-dark)]" />
      )}
      {shortcut && (
        <kbd className="flex-shrink-0 text-[11px] font-mono text-[var(--color-text-muted)] bg-[var(--color-surface-hover)] px-1.5 py-0.5 rounded border border-[var(--color-border-light)]">
          {shortcut}
        </kbd>
      )}
    </button>
  );
}

// ─── Toggle ──────────────────────────────────────────────

export interface ActionSheetToggleProps {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

function Toggle({ label, description, checked, onChange }: ActionSheetToggleProps) {
  const switchId = `action-toggle-${label.replace(/\s/g, "-")}`;

  return (
    <label
      htmlFor={switchId}
      className={[
        "flex items-center gap-3 px-3 rounded-lg cursor-pointer",
        "min-h-[44px] py-2.5",
        "transition-colors duration-150",
        "hover:bg-[var(--color-surface-hover)]",
        "active:bg-[var(--color-surface-hover)]",
        "select-none",
      ].join(" ")}
    >
      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium text-[var(--color-text-primary)] leading-snug">
          {label}
        </span>
        {description && (
          <span className="block text-xs text-[var(--color-text-muted)] mt-0.5 leading-relaxed">
            {description}
          </span>
        )}
      </div>
      {/* Switch - matches Switch.tsx pattern with focus ring */}
      <div className="relative inline-flex h-6 w-11 items-center flex-shrink-0">
        <input
          type="checkbox"
          id={switchId}
          className="peer sr-only"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span
          className={[
            "block w-11 h-6 rounded-full transition-colors duration-200",
            "bg-[var(--color-border)]",
            "peer-checked:bg-[var(--color-accent-yellow-dark)]",
            "peer-focus-visible:ring-2 peer-focus-visible:ring-[var(--color-accent-yellow)] peer-focus-visible:ring-offset-1",
          ].join(" ")}
        />
        <span
          className={[
            "absolute left-1 top-1 w-4 h-4 rounded-full",
            "bg-white shadow-sm",
            "transition-transform duration-200 ease-[var(--ease-bounce)]",
            "peer-checked:translate-x-5",
          ].join(" ")}
        />
      </div>
    </label>
  );
}

// ─── Stepper ─────────────────────────────────────────────

export interface ActionSheetStepperProps {
  label: string;
  value: number;
  unit?: string;
  step?: number;
  min?: number;
  max?: number;
  onChange: (value: number) => void;
}

function Stepper({
  label,
  value,
  unit = "px",
  step = 30,
  min = 0,
  max,
  onChange,
}: ActionSheetStepperProps) {
  const atMin = value <= min;
  const atMax = max !== undefined && value >= max;

  return (
    <div className="flex items-center justify-between px-3 min-h-[44px] py-2 rounded-lg bg-[var(--color-surface-hover)]">
      <span className="text-sm font-medium text-[var(--color-text-secondary)]">{label}</span>
      <div className="flex items-center gap-1.5">
        <button
          onClick={() => onChange(Math.max(min, value - step))}
          disabled={atMin}
          className={[
            "w-9 h-9 flex items-center justify-center rounded-lg",
            "border border-[var(--color-border)]",
            "bg-[var(--color-bg-primary)]",
            "transition-all duration-150",
            atMin
              ? "opacity-30 cursor-not-allowed"
              : [
                  "text-[var(--color-text-secondary)]",
                  "hover:bg-[var(--color-surface-hover)] hover:border-[var(--color-text-muted)]",
                  "active:scale-95 active:bg-[var(--color-surface-hover)]",
                ].join(" "),
          ].join(" ")}
        >
          <Minus className="w-3.5 h-3.5" />
        </button>
        <span className="text-xs font-mono text-[var(--color-text-primary)] w-14 text-center tabular-nums select-none">
          {Math.round(value)}{unit}
        </span>
        <button
          onClick={() => onChange(max !== undefined ? Math.min(max, value + step) : value + step)}
          disabled={atMax}
          className={[
            "w-9 h-9 flex items-center justify-center rounded-lg",
            "border border-[var(--color-border)]",
            "bg-[var(--color-bg-primary)]",
            "transition-all duration-150",
            atMax
              ? "opacity-30 cursor-not-allowed"
              : [
                  "text-[var(--color-text-secondary)]",
                  "hover:bg-[var(--color-surface-hover)] hover:border-[var(--color-text-muted)]",
                  "active:scale-95 active:bg-[var(--color-surface-hover)]",
                ].join(" "),
          ].join(" ")}
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// ─── Colors ──────────────────────────────────────────────

const DEFAULT_COLORS: BlockColor[] = [
  "default", "gray", "brown", "orange", "yellow",
  "green", "blue", "purple", "pink", "red",
];

export interface ActionSheetColorsProps {
  label?: string;
  colors?: BlockColor[];
  selected?: BlockColor;
  onSelect: (color: BlockColor | undefined) => void;
}

function Colors({
  label,
  colors = DEFAULT_COLORS,
  selected,
  onSelect,
}: ActionSheetColorsProps) {
  return (
    <div className="px-3 py-2.5">
      {label && (
        <span className="block text-xs font-semibold text-[var(--color-text-muted)] mb-2.5 tracking-wide">
          {label}
        </span>
      )}
      <div className="flex flex-wrap gap-2.5">
        {colors.map((color) => {
          const isSelected = selected === color || (selected === undefined && color === "default");
          return (
            <button
              key={color}
              onClick={() => onSelect(color === "default" ? undefined : color)}
              aria-label={blockColorConfig[color].label}
              title={blockColorConfig[color].label}
              className={[
                "w-8 h-8 rounded-full",
                "border-2 transition-all duration-200 ease-[var(--ease-bounce)]",
                "hover:scale-110",
                "active:scale-95",
                "focus-visible:outline-2 focus-visible:outline-[var(--color-pad-line-blue)] focus-visible:outline-offset-2",
                color === "default"
                  ? "bg-[var(--color-bg-primary)] dark:bg-[var(--color-bg-elevated)]"
                  : blockColorConfig[color].bgClass,
                isSelected
                  ? "border-[var(--color-accent-yellow-dark)] scale-110 shadow-[0_0_0_2px_var(--color-accent-yellow)]"
                  : "border-[var(--color-border)] hover:border-[var(--color-text-muted)]",
              ].join(" ")}
            >
              {isSelected && (
                <Check className="w-3.5 h-3.5 mx-auto text-[var(--color-accent-yellow-dark)]" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Section ─────────────────────────────────────────────

export interface ActionSheetSectionProps {
  title?: string;
  children: ReactNode;
}

function Section({ title, children }: ActionSheetSectionProps) {
  return (
    <div className="py-0.5">
      {title && (
        <div className="px-3 pt-2 pb-1">
          <span className="text-[11px] font-semibold text-[var(--color-text-muted)] uppercase tracking-wider select-none">
            {title}
          </span>
        </div>
      )}
      {children}
    </div>
  );
}

// ─── Divider ─────────────────────────────────────────────

function SheetDivider() {
  return (
    <div className="my-1 mx-3" role="separator">
      <div className="border-t border-[var(--color-border)]" />
    </div>
  );
}

// ─── Tabs ────────────────────────────────────────────────

export interface ActionSheetTabsProps<T extends string> {
  tabs: { key: T; label: string }[];
  active: T;
  onChange: (tab: T) => void;
}

function Tabs<T extends string>({ tabs, active, onChange }: ActionSheetTabsProps<T>) {
  return (
    <div
      className="flex gap-1 mx-2 mb-2 bg-[var(--color-surface-hover)] rounded-lg p-1"
      role="tablist"
    >
      {tabs.map((tab) => {
        const isActive = active === tab.key;
        return (
          <button
            key={tab.key}
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(tab.key)}
            className={[
              "flex-1 min-h-[36px] py-2 text-xs font-semibold rounded-md",
              "transition-all duration-200 ease-[var(--ease-smooth)]",
              "select-none",
              "focus-visible:outline-2 focus-visible:outline-[var(--color-pad-line-blue)] focus-visible:outline-offset-[-2px]",
              isActive
                ? [
                    "bg-[var(--color-bg-primary)] dark:bg-[var(--color-bg-elevated)]",
                    "text-[var(--color-text-primary)]",
                    "shadow-[var(--card-shadow-sm)]",
                  ].join(" ")
                : [
                    "text-[var(--color-text-muted)]",
                    "hover:text-[var(--color-text-secondary)]",
                    "active:bg-[var(--color-surface-hover)]",
                  ].join(" "),
            ].join(" ")}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Search ──────────────────────────────────────────────

export interface ActionSheetSearchProps {
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
}

function Search({ placeholder = "검색", value, onChange }: ActionSheetSearchProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="px-2 pb-2">
      <div className="relative">
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-text-muted)] pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={[
            "w-full pl-9 pr-3 py-2.5 text-sm",
            "bg-[var(--color-surface-hover)]",
            "border border-[var(--color-border)]",
            "rounded-lg",
            "text-[var(--color-text-primary)]",
            "placeholder:text-[var(--color-text-muted)]",
            "transition-all duration-200",
            "focus:outline-none",
            "focus:ring-2 focus:ring-[var(--color-accent-yellow)]/50",
            "focus:border-[var(--color-accent-yellow-dark)]",
            "focus:bg-[var(--color-bg-primary)]",
          ].join(" ")}
          autoFocus
        />
        {value && (
          <button
            onClick={() => {
              onChange("");
              inputRef.current?.focus();
            }}
            className={[
              "absolute right-2 top-1/2 -translate-y-1/2",
              "w-5 h-5 flex items-center justify-center rounded-full",
              "text-[var(--color-text-muted)]",
              "hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-text-secondary)]",
              "transition-colors duration-150",
            ].join(" ")}
            aria-label="검색어 지우기"
          >
            <span className="text-xs leading-none">&times;</span>
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Compound export ─────────────────────────────────────

ActionSheet.Item = Item;
ActionSheet.Toggle = Toggle;
ActionSheet.Stepper = Stepper;
ActionSheet.Colors = Colors;
ActionSheet.Section = Section;
ActionSheet.Divider = SheetDivider;
ActionSheet.Tabs = Tabs;
ActionSheet.Search = Search;
